/**
 * SIL-473 — Circularity tab copy. The panel header, completeness footer, and
 * the "avoided vs new" label/baseline are the SIL-426 §10 advisory-panel
 * strings, imported VERBATIM from the single source of truth
 * (`components/financials/carbon/carbonCopy.ts` — SIL-440's copy inventory
 * transcription) rather than retyped here, per this ticket's own
 * instruction ("import/reuse verbatim, never retype") and the inventory's
 * own rule ("Never reword, abbreviate, or 'tighten' a string here"). The
 * Cat-5 disposal basis note is the SIL-426 §9 string, imported verbatim from
 * `@siliconx/shared` (`dto/esg-export.ts`, the ESG export's own copy source).
 *
 * The repurpose captions and disposal-column caption below are NOT in the
 * SIL-426 inventory (that inventory covers the Financials scenario-forecast
 * carbon surfaces; this is a different domain, the Environmental
 * dashboard's historical Circularity tab) — they are new copy, written to
 * the same conventions: never "credit"/"offset"/"carbon neutral", advisory
 * framing, no conformance claim (so no "per" vs "consistent with" question
 * arises).
 */
export {
  CARBON_ADVISORY_HEADER as CIRCULARITY_ADVISORY_HEADER,
  CARBON_ADVISORY_FOOTER as CIRCULARITY_ADVISORY_FOOTER,
  CARBON_ADVISORY_AVOIDED_VS_NEW_LABEL as CIRCULARITY_AVOIDED_VS_NEW_LABEL,
  CARBON_ADVISORY_AVOIDED_VS_NEW_BASELINE as CIRCULARITY_AVOIDED_VS_NEW_BASELINE,
} from '../../financials/carbon/carbonCopy';
export { ESG_DISPOSAL_ADDER_BASIS_NOTE as CIRCULARITY_CAT5_BURDEN_BASIS_NOTE } from '@siliconx/shared';

/** Sankey resale-leg terminal annotation label. */
export const CIRCULARITY_RESALE_LEG_LABEL = 'Resale';

/** Sankey repurpose-leg terminal annotation. */
export const CIRCULARITY_REPURPOSE_LEG_LABEL = 'Repurpose (redeploy / extend)';
export const CIRCULARITY_EMBODIED_DEFERRED_LABEL = 'Embodied deferred';
export const CIRCULARITY_EMBODIED_DEFERRED_BASELINE =
  'Baseline: the same capacity achieved by replacing now with new equivalent hardware. Reflects the embodied carbon already carried by assets kept in service rather than disposed of or replaced this period.';

/** Sankey disposal-leg terminal annotation. */
export const CIRCULARITY_DISPOSAL_LEG_LABEL = 'Disposal';
export const CIRCULARITY_CAT5_BURDEN_LABEL = 'Cat-5 disposal burden';
/** No avoided or displaced figure ships alongside this number — see
 * `CIRCULARITY_CAT5_BURDEN_BASIS_NOTE` (SIL-426 §9, verbatim) for why. */
export const CIRCULARITY_NO_AVOIDED_FIGURE_NOTE = 'Never an avoided or displaced figure.';

/**
 * SIL-516 — outcome-stage (stage 3) node copy for the multi-stage Sankey.
 * `CIRCULARITY_REUSE_BURDENS_LABEL` is the non-advisory sibling of the
 * quarantined "Avoided vs new" node: `embodiedKgco2e − avoidedVsNewKgco2e`
 * on the resale leg — transport + refurbishment burden that resale does NOT
 * avoid, kept outside the SIL-459 quarantine because it is a burden, not an
 * avoided-emissions claim.
 */
export const CIRCULARITY_REUSE_BURDENS_LABEL = 'Reuse burdens';
export const CIRCULARITY_REUSE_BURDENS_CAPTION = 'Transport + refurbishment — the embodied carbon resale does not avoid.';
export const CIRCULARITY_RETAINED_IN_FLEET_LABEL = 'Retained in fleet';
export const CIRCULARITY_RETAINED_IN_FLEET_CAPTION = 'Embodied stock, cat 1/2 — carried forward, not re-emitted.';
export const CIRCULARITY_FLEET_EMBODIED_LABEL = 'Fleet embodied';

/** Disposition-comparison column headers + captions. */
export const CIRCULARITY_COMPARISON_TITLE = 'Disposition comparison — representative gpu node';
export const CIRCULARITY_RESALE_COLUMN_LABEL = 'Resale';
export const CIRCULARITY_REPURPOSE_COLUMN_LABEL = 'Repurpose';
export const CIRCULARITY_DISPOSAL_COLUMN_LABEL = 'Disposal';

export const CIRCULARITY_REPURPOSE_BASELINE =
  'Baseline: the same capacity achieved by replacing now with new equivalent hardware. New-equivalent embodied carbon avoided, minus any operational-efficiency delta of continuing to run the existing unit (not modeled in this version — held at zero and disclosed).';

export const CIRCULARITY_COMPONENT_HARVEST_LABEL = 'Component harvest';
export const CIRCULARITY_COMPONENT_HARVEST_BASELINE =
  'Baseline: new-component purchase. Estimated avoided embodied carbon from harvesting reusable components (memory, storage, fans) off this unit instead of buying new ones — a platform-modeled convention, not a GHG Protocol figure.';

export const CIRCULARITY_DISPOSAL_COLUMN_CAPTION =
  'The un-numbered floor — Cat-5 disposal burden only, never an avoided or displaced figure.';

/** Avoided-vs-goal bar. */
export const CIRCULARITY_GOAL_BAR_TITLE = 'Avoided to date vs. goal';
export const CIRCULARITY_GOAL_BAR_CAPTION =
  'Advisory vs. advisory — both figures are estimates, never compared against or netted from the footprint forecast.';
